import React from 'react';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend, ChartDataLabels);

interface PriorityData {
  critical_1: number;
  high_2: number;
  moderate_3: number;
  low_4: number;
  planning_5: number;
}

interface ServiceRequestPriorityBarChartProps {
  data: PriorityData;
  onBarClick?: (priority: string) => void;
}

const priorityLabels: Record<string, string> = {
  critical_1: 'Critical',
  high_2: 'High',
  moderate_3: 'Moderate',
  low_4: 'Low',
  planning_5: 'Planning',
};

const priorityColors: Record<string, string> = {
  critical_1: 'rgba(239, 68, 68, 0.8)',   // red
  high_2: 'rgba(249, 115, 22, 0.8)',      // orange
  moderate_3: 'rgba(245, 158, 11, 0.8)',  // amber
  low_4: 'rgba(16, 185, 129, 0.8)',       // green
  planning_5: 'rgba(107, 114, 128, 0.8)', // gray
};

const ServiceRequestPriorityBarChart: React.FC<ServiceRequestPriorityBarChartProps> = ({ data, onBarClick }) => {
  const priorityKeys = Object.keys(priorityLabels);
  const labels = priorityKeys.map(key => priorityLabels[key]);
  const values = priorityKeys.map(key => data[key as keyof PriorityData] || 0);
  const colors = priorityKeys.map(key => priorityColors[key]);
  const total = values.reduce((sum, val) => sum + val, 0);

  const chartData = {
    labels,
    datasets: [
      {
        label: 'Service Requests',
        data: values,
        backgroundColor: colors,
        borderColor: colors.map(c => c.replace('0.8', '1')),
        borderWidth: 1,
        borderRadius: 8,
        barThickness: 50,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => {
            const value = context.raw as number;
            const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return `${value} requests (${percentage}%)`;
          }
        }
      },
      datalabels: {
        display: true,
        color: '#fff',
        font: {
          weight: 'bold' as const,
          size: 14,
        },
        anchor: 'center' as const,
        align: 'center' as const,
        formatter: (value: number) => value > 0 ? value : '',
      }
    },
    scales: {
      x: {
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0, 0, 0, 0.05)' },
        ticks: { precision: 0 }
      }
    },
    onClick: (_event: any, elements: any[]) => {
      if (elements.length > 0 && onBarClick) {
        const index = elements[0].index;
        onBarClick(priorityKeys[index]);
      }
    },
  };

  return (
    <div id="chart-sr-priority" className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Priority-wise Distribution</h3>
        <span className="text-xs text-gray-500">Click to filter</span>
      </div>
      <div className="h-64">
        <Bar data={chartData} options={options} />
      </div>
      <div className="mt-4 flex flex-wrap gap-2 justify-center">
        {priorityKeys.map((priority, index) => (
          <button
            key={priority}
            onClick={() => onBarClick?.(priority)}
            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium hover:opacity-80 transition-opacity"
            style={{ backgroundColor: priorityColors[priority], color: 'white' }}
          >
            {priorityLabels[priority]}: {values[index]}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ServiceRequestPriorityBarChart;
